import React from 'react';
import { Segmented, Tag, Tooltip } from 'antd';
import { PTF_RETURN_ANALYZED, HISTORICAL_SCENARIO } from 'state';
import type { LLMGroupResponseType } from 'state';
import type { Studio2Player, Studio2Session } from '../live/types';
import type { ScatterPoint } from './RiskReturnScatter';
import type { RelativizeDate } from '../helpers';
import { pct, money, signClass, signedMoney, BAND_COLORS } from '../helpers';
import BandChip from './BandChip';
import MiniChart from './MiniChart';
import SnapshotPanel from './detail/SnapshotPanel';
import DriversPanel from './detail/DriversPanel';
import RiskPanel from './detail/RiskPanel';
import BehaviorPanel from './detail/BehaviorPanel';
import BiasPanel from './detail/BiasPanel';
import DecisionsPanel from './detail/DecisionsPanel';
import ReportPanel from './detail/ReportPanel';

export type DetailTab =
  | 'snapshot'
  | 'drivers'
  | 'risk'
  | 'behavior'
  | 'bias'
  | 'decisions'
  | 'report';

/** 'cohort' = trainer view (whole roster, ranks, cohort scatter, group report).
 *  'self' = per-player trainee view (own slice only; rank / cohort tags hidden,
 *  the scatter uses anonymised points, the group report toggle is removed). */
export type StudioMode = 'cohort' | 'self';

const TABS = [
  { label: 'Snapshot', value: 'snapshot' },
  { label: 'Drivers', value: 'drivers' },
  { label: 'Risk', value: 'risk' },
  { label: 'Behavior', value: 'behavior' },
  { label: 'Bias', value: 'bias' },
  { label: 'Decisions', value: 'decisions' },
  { label: 'Report', value: 'report' },
];

type Props = {
  player: Studio2Player;
  cohort: Studio2Player[];
  groupReports: LLMGroupResponseType[];
  session: Studio2Session;
  tab: DetailTab;
  onTab: (t: DetailTab) => void;
  mode?: StudioMode;
  // Self view only: anonymised cohort scatter points (see RiskPanel).
  scatterPoints?: ScatterPoint[];
  // Self + historical only: relativises scenario dates to "Day N" in the panels
  // that render them (Decisions, Drivers positioning overlay).
  relativizeDate?: RelativizeDate;
  // Tabs to render disabled (greyed + hover popover, not selectable). Self view
  // gates 'bias'/'report' behind the session flag; empty for the trainer.
  lockedTabs?: DetailTab[];
};

const LOCKED_TAB_TITLE: Partial<Record<DetailTab, string>> = {
  bias: 'Bias analysis not yet available',
  report: 'Final report not yet available',
};
const HIDDEN_DATE_FALLBACK: RelativizeDate = () => 'Day —';

const overallBand = (p: Studio2Player): number | null => {
  const m = p.analysis.analyzed_metrics.find(
    a => a.analyzed_metric === PTF_RETURN_ANALYZED,
  );
  return m ? m.band : null;
};

const DetailView: React.FC<Props> = ({
  player,
  cohort,
  groupReports,
  session,
  tab,
  onTab,
  mode = 'cohort',
  scatterPoints,
  relativizeDate,
  lockedTabs,
}) => {
  const isSelf = mode === 'self';
  const isLocked = (t: DetailTab) => !!lockedTabs?.includes(t);
  // Historical self view supplies the real Day-N relativizer. Keep a safe
  // non-calendar fallback for any other trainee state so this chart can never
  // reveal an absolute date merely because the anchor is unavailable.
  const traineeDv01Date = isSelf
    ? (relativizeDate ?? HIDDEN_DATE_FALLBACK)
    : undefined;

  // Hide the Risk tab entirely when the session has no benchmark of any kind —
  // its headline metrics (alpha/beta/Sharpe/Treynor) are benchmark-derived and
  // would be meaningless zeros. Same gate as the Return-vs-benchmark tile.
  const visibleTabs = session.has_benchmark
    ? TABS
    : TABS.filter(t => t.value !== 'risk');

  // Locked tabs render greyed (CSS) with a hover popover and are not selectable.
  const tabOptions = visibleTabs.map(t => {
    if (!isLocked(t.value as DetailTab)) return t;
    return {
      ...t,
      label: (
        <Tooltip title={LOCKED_TAB_TITLE[t.value as DetailTab]}>
          <span className="studio2__tab--locked">{t.label}</span>
        </Tooltip>
      ),
    };
  });
  const spark = player.player_history.map(h =>
    Number((h.portfolio_return * 100).toFixed(3)),
  );
  const sparkUp = player.last_portfolio_return >= 0;

  return (
    <>
      <div className="studio2__player-header">
        <div className="studio2__player-id">
          <span
            className={`studio2__dot ${player.online ? 'studio2__dot--online' : ''}`}
          />
          <span className="studio2__player-name">{player.name}</span>
          <div className="studio2__player-tags">
            {!isSelf && (
              <Tag>
                #{player.report.returns.rank} / {cohort.length}
              </Tag>
            )}
            <Tooltip title="Overall band (portfolio return)">
              <span>
                <BandChip band={overallBand(player)} />
              </span>
            </Tooltip>
            <Tag color={player.online ? 'green' : 'default'}>
              {player.online ? 'Online' : 'Offline'}
            </Tag>
          </div>
        </div>

        <div className="studio2__player-quickstats">
          <div className="studio2__quickstat">
            <span>Return</span>
            <strong className={signClass(player.last_portfolio_return)}>
              {pct(player.last_portfolio_return)}
            </strong>
          </div>
          <div className="studio2__quickstat">
            <span>Total PnL</span>
            <strong className={signClass(player.last_pnl_sum)}>
              {signedMoney(player.last_pnl_sum)}
            </strong>
          </div>
          <div className="studio2__quickstat">
            <span>Turnover</span>
            <strong>{money(player.total_turnover)}</strong>
          </div>
          <div className="studio2__player-spark">
            <MiniChart
              type="area"
              sparkline
              height={40}
              series={[{ data: spark }]}
              colors={[sparkUp ? BAND_COLORS[0] : BAND_COLORS[3]]}
            />
          </div>
        </div>
      </div>

      <div className="studio2__subtabs">
        <Segmented
          value={tab}
          onChange={v => {
            // Ignore clicks on locked tabs (controlled value stays put).
            if (isLocked(v as DetailTab)) return;
            onTab(v as DetailTab);
          }}
          options={tabOptions}
        />
        <span className="studio2__kbdhint">
          <span className="studio2__kbd">←</span>
          <span className="studio2__kbd">→</span>
          switch tabs
        </span>
      </div>

      <div className="studio2__panel">
        {tab === 'snapshot' && (
          <SnapshotPanel
            player={player}
            cohortSize={isSelf ? undefined : cohort.length}
            session={session}
          />
        )}
        {tab === 'drivers' && (
          <DriversPanel player={player} relativizeDate={relativizeDate} />
        )}
        {tab === 'risk' && (
          <RiskPanel
            player={player}
            cohort={cohort}
            session={session}
            scatterPoints={isSelf ? scatterPoints : undefined}
            showAfsDv01={!isSelf}
            relativizeDate={traineeDv01Date}
          />
        )}
        {tab === 'behavior' && <BehaviorPanel player={player} />}
        {tab === 'bias' &&
          (isLocked('bias') ? (
            <div className="studio2__locked-panel">{LOCKED_TAB_TITLE.bias}</div>
          ) : (
            <BiasPanel player={player} />
          ))}
        {tab === 'decisions' && (
          <DecisionsPanel
            player={player}
            isHistorical={session.scenario_type === HISTORICAL_SCENARIO}
            relativizeDate={relativizeDate}
          />
        )}
        {tab === 'report' &&
          (isLocked('report') ? (
            <div className="studio2__locked-panel">
              {LOCKED_TAB_TITLE.report}
            </div>
          ) : (
            <ReportPanel
              player={player}
              groupReports={groupReports}
              showGroup={!isSelf}
            />
          ))}
      </div>
    </>
  );
};

export default DetailView;
